'use client';

import type { MysteryRoomCustomObjectDefinition, MysteryRoomVec2 } from './mapTypes';

type AlphaMask = {
  width: number;
  height: number;
  alpha: Uint8ClampedArray;
};

const alphaMasks = new Map<string, AlphaMask>();
const DEFAULT_ALPHA_THRESHOLD = 24;

export function mysteryRoomAssetAlphaMaskKey(object: MysteryRoomCustomObjectDefinition): string | null {
  const source = object.assetId || object.assetUrl;
  return source ? `${object.id}:${source}` : null;
}

export function registerMysteryRoomCustomObjectAlphaMask(
  object: MysteryRoomCustomObjectDefinition,
  imageSource: unknown,
): boolean {
  const key = mysteryRoomAssetAlphaMaskKey(object);
  if (!key || !imageSource || typeof document === 'undefined') {
    return false;
  }

  const width = imageSourceWidth(imageSource);
  const height = imageSourceHeight(imageSource);
  if (width <= 0 || height <= 0) {
    return false;
  }

  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      return false;
    }
    context.drawImage(imageSource as CanvasImageSource, 0, 0, width, height);
    const data = context.getImageData(0, 0, width, height).data;
    const alpha = new Uint8ClampedArray(width * height);
    for (let index = 0, alphaIndex = 0; index < data.length; index += 4, alphaIndex += 1) {
      alpha[alphaIndex] = data[index + 3] ?? 0;
    }
    alphaMasks.set(key, { width, height, alpha });
    return true;
  } catch {
    return false;
  }
}

export function isMysteryRoomCustomObjectAlphaOpaqueAtPoint(
  object: MysteryRoomCustomObjectDefinition,
  point: MysteryRoomVec2,
): boolean | null {
  if (!object.assetAlphaCollisionEnabled || !object.assetUrl) {
    return null;
  }
  const key = mysteryRoomAssetAlphaMaskKey(object);
  const mask = key ? alphaMasks.get(key) : null;
  if (!mask) {
    return null;
  }

  const imagePoint = worldPointToAssetPixel(object, point, mask.width, mask.height);
  if (!imagePoint) {
    return false;
  }
  const pixelX = Math.max(0, Math.min(mask.width - 1, Math.floor(imagePoint.x)));
  const pixelY = Math.max(0, Math.min(mask.height - 1, Math.floor(imagePoint.y)));
  const alpha = mask.alpha[pixelY * mask.width + pixelX] ?? 0;
  return alpha >= DEFAULT_ALPHA_THRESHOLD;
}

function worldPointToAssetPixel(
  object: MysteryRoomCustomObjectDefinition,
  point: MysteryRoomVec2,
  imageWidth: number,
  imageHeight: number,
): MysteryRoomVec2 | null {
  const rect = object.rect;
  const objectCenter = { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
  const objectRotation = degreesToRadians(-(object.rotation ?? 0));
  const objectLocal = rotatePointAround(point, objectCenter, objectRotation);
  const localX = objectLocal.x - rect.x;
  const localY = objectLocal.y - rect.y;

  if (localX < 0 || localY < 0 || localX > rect.width || localY > rect.height) {
    return null;
  }

  const transform = object.assetTransform ?? {};
  const offsetX = finiteNumber(transform.offsetX, 0);
  const offsetY = finiteNumber(transform.offsetY, 0);
  const scaleX = positiveNumber(transform.scaleX, 1);
  const scaleY = positiveNumber(transform.scaleY, 1);
  const imageRotation = degreesToRadians(-finiteNumber(transform.rotation, 0));
  const imageCenter = {
    x: rect.width / 2 + offsetX,
    y: rect.height / 2 + offsetY,
  };
  const pointInImageSpace = rotatePointAround({ x: localX, y: localY }, imageCenter, imageRotation);
  const display = assetBaseDisplaySize(object.assetFit, rect.width, rect.height, imageWidth, imageHeight);
  const normalizedX = (pointInImageSpace.x - imageCenter.x) / scaleX + display.width / 2;
  const normalizedY = (pointInImageSpace.y - imageCenter.y) / scaleY + display.height / 2;
  if (normalizedX < 0 || normalizedY < 0 || normalizedX > display.width || normalizedY > display.height) {
    return null;
  }

  return {
    x: (normalizedX / Math.max(1, display.width)) * imageWidth,
    y: (normalizedY / Math.max(1, display.height)) * imageHeight,
  };
}

function assetBaseDisplaySize(
  fit: MysteryRoomCustomObjectDefinition['assetFit'],
  objectWidth: number,
  objectHeight: number,
  imageWidth: number,
  imageHeight: number,
) {
  if (fit === 'stretch') {
    return { width: objectWidth, height: objectHeight };
  }
  const scale =
    fit === 'cover'
      ? Math.max(objectWidth / Math.max(1, imageWidth), objectHeight / Math.max(1, imageHeight))
      : Math.min(objectWidth / Math.max(1, imageWidth), objectHeight / Math.max(1, imageHeight));
  return {
    width: imageWidth * scale,
    height: imageHeight * scale,
  };
}

function rotatePointAround(point: MysteryRoomVec2, center: MysteryRoomVec2, radians: number): MysteryRoomVec2 {
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  };
}

function imageSourceWidth(source: any): number {
  if ('naturalWidth' in source && typeof source.naturalWidth === 'number') {
    return source.naturalWidth;
  }
  if ('videoWidth' in source && typeof source.videoWidth === 'number') {
    return source.videoWidth;
  }
  return Number(source.width) || 0;
}

function imageSourceHeight(source: any): number {
  if ('naturalHeight' in source && typeof source.naturalHeight === 'number') {
    return source.naturalHeight;
  }
  if ('videoHeight' in source && typeof source.videoHeight === 'number') {
    return source.videoHeight;
  }
  return Number(source.height) || 0;
}

function degreesToRadians(degrees: number): number {
  return (degrees / 180) * Math.PI;
}

function finiteNumber(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function positiveNumber(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
}
